import { useEffect } from 'react'
import { useCloseOnSelectionChange } from './useCloseOnSelectionChange'
import { createPortal } from 'react-dom'
import { Sparkles, X, Type, CircleDot } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button, cn } from '@teach/ui'
import {
  ACTIVE_BLOCK_CARDS,
  type ActiveBlockCard,
  type BlockCardCategory,
} from '@/features/editor/constants/blockTypeMatrix'
import { AddBlockTile, type TileData } from './AddBlockTile'
import { AddBlockPicker } from './AddBlockPicker'
import type { LessonType } from '@/types/lesson'
import type { NewBlockPreview } from '@/features/editor/lib/newBlockPreview'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** id слота на холсте редактора, куда встаёт выбор типа нового экрана. */
export const CANVAS_ADD_SLOT_ID = 'canvas-add-block-slot'

interface Props {
  onAdd: (card: ActiveBlockCard) => void
  onClose: () => void
  lessonType: LessonType
  previewFor?: (card: ActiveBlockCard) => NewBlockPreview | null
}

const SECTIONS: Array<{
  heading: 'sectionContent' | 'sectionInteractive'
  category: BlockCardCategory
  icon: LucideIcon
  iconClass: string
}> = [
  {
    heading: 'sectionContent',
    category: 'content',
    icon: Type,
    iconClass: 'text-muted-foreground',
  },
  {
    heading: 'sectionInteractive',
    category: 'interactive',
    icon: CircleDot,
    iconClass: 'text-success',
  },
]

/**
 * Меню живёт ПОРТАЛОМ в `document.body` — как радиксовые диалоги, у которых
 * этой болезни нет. Единственный вход в него (`BlockStrip`) отрисован внутри
 * футера ленты уроков, а тот несёт `backdrop-blur-sm`: `backdrop-filter` (как
 * и `filter`) создаёт containing block для `position: fixed`-потомков, поэтому
 * `inset-0` резолвился в границы футера (159px вместо вьюпорта), а
 * `overflow-x-auto` дорезал остальное — меню схлопывалось в полосу, и добавить
 * блок было нельзя вовсе (живой прогон: `elementFromPoint` в точке плитки
 * отдавал соседнюю панель). Ни z-index, ни снятие блюра с футера (осознанная
 * реф-парити) это не лечат — лечит только вынос из-под предка с фильтром.
 */
export const AddBlockMenu = ({ onAdd, onClose, lessonType, previewFor }: Props) => {
  const m = useMessages(messages)
  // Escape: у рукописного модала своего обработчика нет (у радиксовых он в
  // коробке), а закрывалось меню только кликом по подложке. Слушатель на
  // документе — фокус внутрь меню не переводится (фокус-трапа тут нет).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useCloseOnSelectionChange(onClose)

  // Выбор типа — прямо на холсте, по центру, где стоит экран урока (слот в
  // `EditorWorkspace`). Вне редактора слота нет — тогда по-старому, окном поверх всего.
  const slot = document.getElementById(CANVAS_ADD_SLOT_ID)
  if (slot) {
    return createPortal(
      <div
        data-testid="add-block-menu"
        // z-[60] — выше быстрой панели элемента составного блока (z-40 у
        // `SortableDesignElement`): она торчала поверх окна выбора.
        className="absolute inset-0 z-[60] flex justify-center bg-background p-4"
        onClick={onClose}
      >
        <AddBlockPicker
          lessonType={lessonType}
          previewFor={previewFor}
          onClose={onClose}
          onAdd={(card) => {
            onAdd(card)
            onClose()
          }}
        />
      </div>,
      slot,
    )
  }
  return createPortal(
    <div
      data-testid="add-block-menu"
      className={cn(
        'flex items-center justify-center p-4 animate-fade-up',
        slot
          ? 'absolute inset-0 z-20 bg-background/95'
          : 'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm',
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-card animate-scale-in',
          slot ? 'max-h-full' : 'max-h-[85vh] border border-border shadow-2xl',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
              <Sparkles className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{m.addBlock}</h2>
              <p className="text-xs text-muted-foreground">{m.chooseContentType}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={m.close}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-8 overflow-y-auto p-6">
          {SECTIONS.map(({ heading, category, icon: HeadingIcon, iconClass }) => {
            const isBlocked = (c: ActiveBlockCard) =>
              lessonType === 'practical' && c.theoreticalOnly === true
            const inCategory = ACTIVE_BLOCK_CARDS.filter((c) => c.category === category)
            const active = inCategory.filter((c) => !isBlocked(c))
            // Типов «скоро» не показываем — только то, что добавить можно сейчас.
            const disabled: TileData[] = inCategory.filter(isBlocked)
            if (active.length === 0 && disabled.length === 0) return null
            return (
              <div key={category}>
                <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-foreground">
                  <HeadingIcon className={cn('h-4 w-4', iconClass)} />
                  {m[heading]}
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {active.map((card) => (
                    <AddBlockTile
                      key={card.id}
                      card={card}
                      onClick={() => {
                        onAdd(card)
                        onClose()
                      }}
                    />
                  ))}
                  {disabled.map((card) => (
                    <AddBlockTile key={card.id} card={card} disabled />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>,
    slot ?? document.body,
  )
}
